import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { API_BASE } from "@/lib/api";
import confetti from "canvas-confetti";
import { useAuth } from "@/context/AuthContext";
import StudentLayout from "@/components/StudentLayout";
import LeaderboardCard from "@/components/LeaderboardCard";
import NextUpBanner from "@/components/NextUpBanner";
import {
  ClipboardList, BookOpen, CalendarDays, Video, Sparkles,
  TrendingUp, ChevronRight, Clock3, Sunrise, Sun, ExternalLink, Lock, PartyPopper, FileText, BarChartBig, Megaphone,
} from "lucide-react";
import { to12h } from "@/lib/time";
import { toast } from "sonner";

function todayISO() {
  const d = new Date();
  const tz = d.getTimezoneOffset();
  return new Date(d.getTime() - tz * 60_000).toISOString().slice(0, 10);
}
function nowHM() { const d = new Date(); return d.getHours() * 60 + d.getMinutes(); }
function parseHM(s) { const [h, m] = (s || "0:0").split(":").map((x) => parseInt(x, 10) || 0); return h * 60 + m; }

export default function StudentDashboard() {
  const { student } = useAuth();
  const navigate = useNavigate();
  const today = todayISO();

  const [att, setAtt] = useState(null);
  const [hw, setHw] = useState([]);
  const [tt, setTt] = useState([]);
  const [oc, setOc] = useState([]);
  const [exams, setExams] = useState([]);
  const [notices, setNotices] = useState([]);
  const [classmates, setClassmates] = useState(null);
  const [classTeacher, setClassTeacher] = useState(null);
  const [isBirthday, setIsBirthday] = useState(false);
  const [pollData, setPollData] = useState(null);
  const [loading, setLoading] = useState(true);
  const partyFired = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const ping = () => api.get("/student/classmates-online").then(r => { if (!cancelled) setClassmates(r.data); }).catch(() => {});
    ping();
    api.get("/student/me").then(r => {
      if (cancelled) return;
      setClassTeacher(r.data?.class_teacher || null);
      const dob = r.data?.dob || "";
      if (dob && dob.slice(5, 10) === today.slice(5, 10)) setIsBirthday(true);
    }).catch(() => {});
    api.get("/student/polls").then(r => {
      if (cancelled) return;
      setPollData(r.data);
      // Reminder nudge: a poll closing within 24h that this student hasn't voted
      // on — toast once per poll per day.
      const soon = (r.data?.active || []).find(p => p.closing_soon);
      if (soon) {
        const key = `sa_poll_nudge_${soon.id}_${today}`;
        if (!localStorage.getItem(key)) {
          localStorage.setItem(key, "1");
          toast.warning(`“${soon.title}” closes soon — you haven't voted yet!`, {
            duration: 8000,
            action: { label: "Vote now", onClick: () => navigate("/student/polls") },
          });
        }
      }
    }).catch(() => {});
    const id = setInterval(ping, 30_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [today]);

  // Birthday celebration: confetti "blasters" across the screen — fires on
  // every visit to the dashboard on the student's birthday.
  useEffect(() => {
    if (!isBirthday || partyFired.current) return;
    partyFired.current = true;

    const colors = ["#f43f5e", "#f59e0b", "#10b981", "#3b82f6", "#a855f7", "#ec4899"];
    const fire = (x, angle) => confetti({
      particleCount: 60, spread: 70, startVelocity: 55, angle,
      origin: { x, y: 0.9 }, colors, zIndex: 9999, scalar: 1.1,
    });
    // Left + right cannon blasts, then repeated bursts for a few seconds.
    fire(0.05, 60); fire(0.95, 120);
    const end = Date.now() + 3500;
    const timer = setInterval(() => {
      if (Date.now() > end) { clearInterval(timer); return; }
      confetti({ particleCount: 40, spread: 100, startVelocity: 45, origin: { x: Math.random(), y: Math.random() * 0.3 }, colors, zIndex: 9999 });
    }, 400);
    return () => clearInterval(timer);
  }, [isBirthday, today]);

  useEffect(() => {
    let cancelled = false;

    const load = async (isInitial) => {
      if (isInitial) setLoading(true);
      const promises = [
        api.get("/student/attendance", { params: { days: 30 } }).then(r => r.data).catch(() => null),
        api.get("/student/homework", { params: { date: today } }).then(r => r.data?.items || []).catch(() => []),
        api.get("/student/timetable", { params: { date: today } }).then(r => r.data?.periods || []).catch(() => []),
        api.get("/student/online-classes", { params: { date: today } }).then(r => r.data?.items || []).catch(() => []),
        api.get("/student/exams").then(r => r.data?.exams || []).catch(() => []),
        api.get("/student/notices").then(r => r.data?.notices || []).catch(() => []),
      ];
      const [a, h, t, o, ex, ns] = await Promise.all(promises);
      if (cancelled) return;
      setAtt(a); setHw(h); setTt(t); setOc(o); setExams(ex); setNotices(ns);
      if (isInitial) setLoading(false);
    };

    load(true);
    // Auto-refresh every 60s so "live now" badges stay accurate without a manual reload.
    const id = setInterval(() => load(false), 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [today]);

  const now = nowHM();
  const liveClasses = oc.filter((c) => { const s = parseHM(c.start_time), e = parseHM(c.end_time); return now >= s && now <= e; });
  const upcomingClasses = oc.filter((c) => now < parseHM(c.start_time));
  const amCount = tt.filter((p) => (p.block || (parseInt((p.start_time||"0").split(":")[0],10) < 13 ? "AM":"PM")) === "AM").length;
  const pmCount = tt.length - amCount;
  const nextPeriod = tt.find((p) => now < parseHM(p.start_time || p.time || "0:0"));
  const pct = att?.summary?.percentage ?? 0;
  const pctTone = pct >= 90 ? "emerald" : pct >= 75 ? "sky" : "rose";
  const pendingTests = exams.filter((e) => e.status === "pending" && !e.submitted);
  const fmtDue = (iso) => {
    try { return new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }); }
    catch { return iso; }
  };
  const dueTone = (iso) => {
    const ms = new Date(iso).getTime() - Date.now();
    if (ms < 0) return "text-rose-600";
    if (ms < 24 * 3600_000) return "text-rose-500";
    if (ms < 3 * 24 * 3600_000) return "text-amber-600";
    return "text-slate-500";
  };

  return (
    <StudentLayout>
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-2">Student dashboard</div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100" style={{fontFamily:"Outfit"}}>Hi {student?.name?.split(" ")[0]}, here&apos;s your day</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">Class {student?.class_name} · Section {student?.section} · Roll #{student?.roll_number} · {today}</p>
          {isBirthday && (
            <div
              className="mt-3 inline-flex items-center gap-3 rounded-2xl bg-gradient-to-r from-pink-500 via-fuchsia-500 to-amber-400 text-white pl-2 pr-5 py-2 shadow-lg animate-in fade-in slide-in-from-left-2 duration-500"
              data-testid="birthday-banner"
            >
              <div className="h-12 w-12 rounded-full ring-2 ring-white/80 overflow-hidden bg-white/20 flex items-center justify-center shrink-0" data-testid="birthday-photo">
                {student?.has_photo ? (
                  <img src={`${API_BASE}/profile/photo/${student.id}`} alt={student?.name} className="h-full w-full object-cover" />
                ) : (
                  <PartyPopper className="h-6 w-6 text-white" />
                )}
              </div>
              <div className="leading-tight">
                <div className="text-base font-bold">Happy Birthday, {student?.name?.split(" ")[0]}! 🎉</div>
                <div className="text-xs font-medium text-white/90">Wishing you a wonderful day from all of us at Trishul!</div>
              </div>
            </div>
          )}
          {classTeacher?.name && (
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5" data-testid="dashboard-class-teacher">Class teacher: <span className="font-medium text-slate-700 dark:text-slate-200">{classTeacher.name}</span></p>
          )}
          {classmates && classmates.online > 0 && (
            <div
              className="mt-3 inline-flex items-center gap-2 rounded-full bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-900 pl-2.5 pr-3.5 py-1.5"
              data-testid="classmates-online-pill"
            >
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
              </span>
              <span className="text-sm font-medium text-emerald-800 dark:text-emerald-200" data-testid="classmates-online-text">
                {classmates.online} classmate{classmates.online === 1 ? "" : "s"} studying now
                {classmates.away > 0 ? ` · ${classmates.away} away` : ""}
              </span>
            </div>
          )}
        </div>
        <div className="flex flex-wrap justify-end gap-2.5">
          <NextUpBanner holidayEndpoint="/student/holidays" holidayTo="/student/holidays" eventEndpoint="/student/events" eventTo="/student/events" />
        </div>
      </div>

      {pollData && pollData.pending > 0 && (() => {
        const urgent = pollData.active.find(p => p.closing_soon);
        const featured = urgent || pollData.active[0];
        return (
        <button
          onClick={() => navigate("/student/polls")}
          data-testid="dashboard-poll-card"
          className={`w-full text-left mb-6 rounded-2xl p-5 text-white shadow-lg hover:shadow-xl transition group relative overflow-hidden ${urgent ? "bg-gradient-to-r from-[#7a1d1d] via-[#a53114] to-[#7a1d1d] ring-2 ring-amber-400/70" : "bg-gradient-to-r from-[#0b2a6b] via-[#153e94] to-[#0b2a6b]"}`}
        >
          <div className="absolute -right-6 -top-6 opacity-10">
            <BarChartBig className="h-32 w-32" />
          </div>
          <div className="relative flex items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
              <BarChartBig className="h-6 w-6 text-amber-300" />
            </div>
            <div className="flex-1 min-w-0">
              <div className={`inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5 mb-1.5 ${urgent ? "bg-amber-400 text-[#7a1d1d] animate-pulse" : "bg-amber-400 text-[#0b2a6b]"}`} data-testid="dashboard-poll-badge">
                {urgent
                  ? <><Clock3 className="h-3 w-3" /> Closes soon — vote before it ends!</>
                  : <>{pollData.pending} Poll{pollData.pending === 1 ? "" : "s"} awaiting your vote</>}
              </div>
              <div className="font-bold text-lg leading-snug truncate">{featured?.title}</div>
              {featured?.description && (
                <div className="text-sm text-white/80 mt-0.5 line-clamp-1">{featured.description}</div>
              )}
            </div>
            <span className={`shrink-0 bg-white font-bold text-sm rounded-full px-4 py-2 group-hover:scale-105 transition ${urgent ? "text-[#7a1d1d]" : "text-[#0b2a6b]"}`}>Vote Now</span>
          </div>
        </button>
        );
      })()}

      <LeaderboardCard />

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="student-overview-cards">
        {/* Attendance */}
        <Card onClick={() => navigate("/student/attendance")} testid="overview-card-attendance" tone="sky"
          icon={ClipboardList} title="Attendance" subtitle="Last 30 days">
          {loading ? <Skel/> : (
            <div className="flex items-end justify-between">
              <div>
                <div className={`text-4xl font-bold ${pctTone === "emerald" ? "text-emerald-600" : pctTone === "sky" ? "text-sky-600" : "text-rose-600"}`} style={{fontFamily:"Outfit"}}>{pct}%</div>
                <div className="text-xs text-slate-500 mt-1">{att?.summary?.present_days ?? 0} present · {att?.summary?.absent_days ?? 0} absent <span className="opacity-70">(days)</span></div>
              </div>
              <TrendingUp className="h-5 w-5 text-slate-400"/>
            </div>
          )}
        </Card>

        {/* Homework */}
        <Card onClick={() => navigate("/student/homework")} testid="overview-card-homework" tone="amber"
          icon={BookOpen} title="Homework" subtitle="Due today">
          {loading ? <Skel/> : hw.length === 0 ? (
            <div className="text-sm text-slate-500">🎉 No homework today!</div>
          ) : (
            <>
              <div className="text-4xl font-bold text-amber-600" style={{fontFamily:"Outfit"}}>{hw.length}</div>
              <div className="text-xs text-slate-500 mt-1 line-clamp-2">
                {hw.slice(0, 2).map((h) => `${h.subject}: ${h.title}`).join(" · ")}
                {hw.length > 2 ? ` · +${hw.length - 2} more` : ""}
              </div>
            </>
          )}
        </Card>

        {/* Timetable */}
        <Card onClick={() => navigate("/student/timetable")} testid="overview-card-timetable" tone="violet"
          icon={CalendarDays} title="Timetable" subtitle="Today">
          {loading ? <Skel/> : tt.length === 0 ? (
            <div className="text-sm text-slate-500">No periods scheduled.</div>
          ) : (
            <div className="space-y-2">
              <div className="text-4xl font-bold text-violet-600" style={{fontFamily:"Outfit"}}>{tt.length}<span className="text-base text-slate-500 font-normal"> periods</span></div>
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <span className="inline-flex items-center gap-1"><Sunrise className="h-3 w-3 text-sky-500"/>{amCount} AM</span>
                <span className="text-slate-300">·</span>
                <span className="inline-flex items-center gap-1"><Sun className="h-3 w-3 text-amber-500"/>{pmCount} PM</span>
              </div>
              {nextPeriod && (
                <div className="text-[11px] text-violet-600 dark:text-violet-300 font-medium truncate">
                  Next: {to12h(nextPeriod.start_time || nextPeriod.time)} · {nextPeriod.subject}
                </div>
              )}
            </div>
          )}
        </Card>

        {/* Announcements */}
        <Card onClick={() => navigate("/student/announcements")} testid="overview-card-announcements" tone="rose"
          icon={Megaphone} title="Announcements" subtitle="From school">
          {loading ? <Skel/> : notices.length === 0 ? (
            <div className="text-sm text-slate-500">Nothing new right now.</div>
          ) : (
            <div className="space-y-2">
              <div className="text-4xl font-bold text-rose-600" style={{fontFamily:"Outfit"}}>
                {notices.length}<span className="text-base text-slate-500 font-normal"> {notices.length === 1 ? "message" : "messages"}</span>
              </div>
              <div className="text-[11px] text-rose-600 dark:text-rose-300 font-medium truncate">
                Latest: {notices[0]?.title || "—"}
              </div>
            </div>
          )}
        </Card>

        {/* Online classes */}
        <Card onClick={() => navigate("/student/online-classes")} testid="overview-card-online" tone="emerald"
          icon={Video} title="Online classes" subtitle="Today">
          {loading ? <Skel/> : oc.length === 0 ? (
            <div className="text-sm text-slate-500">🌿 No online classes today.</div>
          ) : (
            <div className="space-y-2">
              {liveClasses.length > 0 ? (
                <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-emerald-500 text-white text-[10px] font-semibold uppercase tracking-[0.15em] animate-pulse">
                  <ExternalLink className="h-3 w-3"/> {liveClasses.length} live now
                </div>
              ) : upcomingClasses.length > 0 ? (
                <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-sky-50 text-sky-700 text-[10px] font-semibold uppercase tracking-[0.15em]">
                  <Lock className="h-3 w-3"/> {upcomingClasses.length} upcoming
                </div>
              ) : (
                <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-slate-100 text-slate-500 text-[10px] font-semibold uppercase tracking-[0.15em]">
                  All ended
                </div>
              )}
              <div className="text-xs text-slate-500 line-clamp-2">
                {oc.slice(0, 2).map((c) => `${to12h(c.start_time)} ${c.subject}`).join(" · ")}
                {oc.length > 2 ? ` · +${oc.length - 2} more` : ""}
              </div>
            </div>
          )}
        </Card>

        {/* Tuitions — landing page live */}
        <Card onClick={() => navigate("/student/tuitions")} testid="overview-card-tuitions" tone="pink"
          icon={Sparkles} title="Tuitions" subtitle="Practice & assess">
          <div className="flex items-center gap-3">
            <div className="text-2xl">📚</div>
            <div className="text-sm text-slate-600 dark:text-slate-300 leading-snug">
              Pick a subject, jump into a lesson, or <span className="text-pink-600 dark:text-pink-300 font-medium">take an assessment</span>.
            </div>
          </div>
        </Card>

        {/* Recent attendance mini-list (read-only summary) */}
        <Card onClick={() => navigate("/student/attendance")} testid="overview-card-recent-att" tone="slate"
          icon={Clock3} title="Recent days" subtitle="Last 5 marked">
          {loading ? <Skel/> : (att?.items || []).length === 0 ? (
            <div className="text-sm text-slate-500">No attendance recorded yet.</div>
          ) : (
            <ul className="space-y-1.5">
              {(att?.items || []).slice(0, 5).map((r) => (
                <li key={`${r.date}-${r.session || "m"}`} className="flex items-center justify-between text-xs">
                  <span className="text-slate-600 dark:text-slate-300 font-mono">{r.date}</span>
                  <span className="text-slate-400 capitalize">{r.session || "morning"}</span>
                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] border ${r.status === "present" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-rose-50 text-rose-700 border-rose-200"}`}>
                    {r.status}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </StudentLayout>
  );
}

function Card({ children, onClick, testid, tone, icon: Icon, title, subtitle }) {
  const tones = {
    sky: "border-sky-200 dark:border-sky-800 hover:border-sky-300",
    amber: "border-amber-200 dark:border-amber-800 hover:border-amber-300",
    violet: "border-violet-200 dark:border-violet-800 hover:border-violet-300",
    emerald: "border-emerald-200 dark:border-emerald-800 hover:border-emerald-300",
    pink: "border-pink-200 dark:border-pink-800 hover:border-pink-300",
    rose: "border-rose-200 dark:border-rose-800 hover:border-rose-300",
    slate: "border-slate-200 dark:border-slate-700 hover:border-slate-300",
  };
  const iconTone = {
    sky: "bg-sky-50 dark:bg-sky-900/40 text-sky-600 dark:text-sky-300",
    amber: "bg-amber-50 dark:bg-amber-900/40 text-amber-600 dark:text-amber-300",
    violet: "bg-violet-50 dark:bg-violet-900/40 text-violet-600 dark:text-violet-300",
    emerald: "bg-emerald-50 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-300",
    pink: "bg-pink-50 dark:bg-pink-900/40 text-pink-600 dark:text-pink-300",
    rose: "bg-rose-50 dark:bg-rose-900/40 text-rose-600 dark:text-rose-300",
    slate: "bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`text-left bg-white dark:bg-slate-800 rounded-2xl border p-5 transition-all hover:shadow-md hover:-translate-y-0.5 ${tones[tone]}`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${iconTone[tone]}`}>
            <Icon className="h-5 w-5" />
          </div>
          <div>
            <div className="text-sm font-semibold text-slate-900 dark:text-slate-100" style={{fontFamily:"Outfit"}}>{title}</div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400">{subtitle}</div>
          </div>
        </div>
        <ChevronRight className="h-4 w-4 text-slate-300" />
      </div>
      <div>{children}</div>
    </button>
  );
}

function Skel() { return <div className="h-10 rounded-lg bg-slate-100 dark:bg-slate-700 animate-pulse"/>; }
